// useTurnstile.ts
import { useEffect, useState, useRef, useCallback } from "react";
import { ENV } from "../config/envConfig";

const TURNSTILE_SCRIPT_URL = ENV.TURNSTILE_SCRIPT_URL;

/* -------------------------------------------------
   Types
-------------------------------------------------- */

type TurnstileErrorCallback = (error: unknown) => void;

interface UseTurnstileResult {
  turnstileRef: React.RefObject<HTMLDivElement | null>;
  token: string | null;
  error: string | null;
  reset: () => void;
}

/* -------------------------------------------------
   Cached Script Loader
-------------------------------------------------- */

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();

  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`);
    if (existing) return resolve();

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Turnstile script"));

    document.head.appendChild(script);
  });

  return scriptPromise;
}

/* -------------------------------------------------
   Hook
-------------------------------------------------- */

export const useTurnstile = (
  siteKey: string | null,
  onError?: TurnstileErrorCallback
): UseTurnstileResult => {
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    if (window?.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
      setToken(null);
      setError(null);
    }
  }, []);

  useEffect(() => {
    if (!siteKey) return;

    let unmounted = false;

    loadTurnstileScript()
      .then(() => {
        if (unmounted || !turnstileRef.current || !window.turnstile) return;

        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: siteKey,
          callback: (token: string) => {
            if (!unmounted) {
              setToken(token);
              setError(null);
            }
          },
          "error-callback": (err: unknown) => {
            if (!unmounted) {
              setToken(null);
              setError("Verification failed. Please try again.");
              onError?.(err);
            }
          },
          "expired-callback": () => {
            if (!unmounted) {
              setToken(null);
              setError("Verification expired. Please try again.");
            }
          },
        });
      })
      .catch((err) => {
        if (!unmounted) setError((err as Error).message);
      });

    return () => {
      unmounted = true;
      if (widgetIdRef.current && window?.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
      }
    };
  }, [siteKey]); // removed onError to avoid unnecessary re-renders

  return { turnstileRef, token, error, reset };
};

export default useTurnstile;
